import { useMemo, useState } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { Header } from "../../../common/Header/Header.jsx";
import { ActionButton, AltButton } from "../../../common/Buttons/Buttons.jsx";
import { LoadSpinner } from "../../../common/LoadSpinner/LoadSpinner.jsx";
import { OperatorCommissionsTable } from "../../../common/Tables/Tables.jsx";
import { notify } from "../../../../assets/helpers/toast.js";
import { filterParams } from "../../../../assets/helpers/funciones.js";
import { parseOperatorCommissionsRows } from "../../../../assets/helpers/operatorCommissions.js";
import { servicesService } from "../../../../services/servicesService.js";
import "./OperatorCommissions.scss";

const MONTH_OPTIONS = [
  { value: 0, name: "Enero" },
  { value: 1, name: "Febrero" },
  { value: 2, name: "Marzo" },
  { value: 3, name: "Abril" },
  { value: 4, name: "Mayo" },
  { value: 5, name: "Junio" },
  { value: 6, name: "Julio" },
  { value: 7, name: "Agosto" },
  { value: 8, name: "Septiembre" },
  { value: 9, name: "Octubre" },
  { value: 10, name: "Noviembre" },
  { value: 11, name: "Diciembre" },
];

const FIRST_YEAR = 2020;

const buildYearOptions = () => {
  const currentYear = moment().year();
  return Array.from(
    { length: currentYear - FIRST_YEAR + 1 },
    (_, index) => currentYear - index
  );
};

const getMonthDateRange = (month, year) => {
  const monthStart = moment().year(year).month(month).startOf("month");
  const monthEnd = moment().year(year).month(month).endOf("month");

  return {
    dateFrom: monthStart.format("YYYY-MM-DD"),
    dateTo: monthEnd.format("YYYY-MM-DD"),
  };
};

export const OperatorCommissions = () => {
  const history = useHistory();
  const now = moment();
  const [selectedMonth, setSelectedMonth] = useState(now.month());
  const [selectedYear, setSelectedYear] = useState(now.year());
  const [commissionsTable, setCommissionsTable] = useState({
    data: [],
    isLoading: false,
    hasCalculated: false,
  });

  const yearOptions = useMemo(() => buildYearOptions(), []);

  const isMonthDisabled = (monthValue) => {
    if (selectedYear < now.year()) return false;
    if (selectedYear > now.year()) return true;
    return monthValue > now.month();
  };

  const handleYearChange = (event) => {
    const year = Number(event.target.value);
    setSelectedYear(year);

    if (year === now.year() && selectedMonth > now.month()) {
      setSelectedMonth(now.month());
    }
  };

  const fetchCommissions = (byMail = false) => {
    if (isMonthDisabled(selectedMonth)) {
      notify("No puede seleccionar un mes futuro", "error");
      return;
    }

    setCommissionsTable((prev) => ({
      ...prev,
      isLoading: true,
    }));

    const { dateFrom, dateTo } = getMonthDateRange(selectedMonth, selectedYear);

    const params = {
      dateFrom,
      dateTo,
    };

    if (byMail) {
      params.byMail = true;
    }

    servicesService
      .getOperatorCommissions(filterParams(params))
      .then((response) => {
        if (response?.status === 200) {
          if (byMail) {
            notify("Recibirá el exportable en su correo electrónico", "success");
            setCommissionsTable((prev) => ({
              ...prev,
              isLoading: false,
            }));
            return;
          }

          setCommissionsTable({
            data: parseOperatorCommissionsRows(response?.data),
            isLoading: false,
            hasCalculated: true,
          });
          return;
        }

        notify(response?.message || "No se pudieron calcular las comisiones", "error");
        setCommissionsTable((prev) => ({
          ...prev,
          isLoading: false,
        }));
      })
      .catch(() => {
        notify("No se pudieron calcular las comisiones", "error");
        setCommissionsTable((prev) => ({
          ...prev,
          isLoading: false,
        }));
      });
  };

  return (
    <>
      <LoadSpinner show={commissionsTable.isLoading} />
      <Header />
      <div className="service-with-container container operator-commissions-page">
        <div className="operator-commissions-page__header">
          <div>
            <h1 className="title-component">Comisiones de Operadores</h1>
            <div className="operator-commissions-page__breadcrumb">
              TU/GRUERO / ESTADÍSTICAS / CONTADOR OPERACIONES / COMISIONES
            </div>
          </div>
          <div className="operator-commissions-page__actions">
            <AltButton
              label="Volver al contador"
              action={() => history.push("/stats/operators-counter")}
            />
            <ActionButton
              label="Exportar"
              action={() => fetchCommissions(true)}
            />
          </div>
        </div>

        <div className="operator-commissions-filters">
          <div className="input-component-container operator-commissions-filter">
            <label htmlFor="commissionMonth">Mes</label>
            <div>
              <select
                id="commissionMonth"
                className="input-select"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(Number(event.target.value))}
              >
                {MONTH_OPTIONS.map((month) => (
                  <option
                    key={month.value}
                    value={month.value}
                    disabled={isMonthDisabled(month.value)}
                  >
                    {month.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="input-component-container operator-commissions-filter">
            <label htmlFor="commissionYear">Año</label>
            <div>
              <select
                id="commissionYear"
                className="input-select"
                value={selectedYear}
                onChange={handleYearChange}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <ActionButton
            label="Calcular comisiones"
            action={() => fetchCommissions(false)}
          />
        </div>

        <OperatorCommissionsTable data={commissionsTable} />
      </div>
    </>
  );
};
